import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Scale, HeartHandshake, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";
import { STORE, IMAGES } from "@/data/store";

const VALUES = [
  { icon: Leaf, title: "Pure Sourcing", text: "Direct from named farms and cooperatives. Every spice, dal and litre of milk has a known origin — ask us and we'll tell you the village." },
  { icon: Scale, title: "Fair Pricing", text: "One rate board, visible to all. We earn a modest margin and sleep well. Bulk buyers get mandi-linked rates in writing." },
  { icon: HeartHandshake, title: "Community Relationship", text: "Khata credit for regulars, doorstep delivery for elders, and a chair by the counter for anyone who wants to talk." },
  { icon: Sparkles, title: "Hygienic Packing", text: "Clean scoops, sealed food-grade packs, gloves for dry fruits, and a shop floor swept and washed twice daily." },
];

const TIMELINE = [
  { year: "1976", text: "Arjun Ram Harijan opens a single-counter kirana with a brass scale, a khata register and one rule: sell only what you'd feed your own family." },
  { year: "1988", text: "The shop doubles in size. Direct relationships with Mathania chilli farmers and Nagaur dal growers begin — partnerships that continue today." },
  { year: "2004", text: "Second generation joins the counter. Cold storage for dairy arrives; the in-store garam masala blend becomes a neighbourhood legend." },
  { year: "2019", text: "Free local delivery starts, and bulk supply to weddings, hostels and hotels becomes a formal service with written mandi-linked quotes." },
  { year: "Today", text: "Three generations of customers shop side by side. The scale is digital now — the honesty behind it is unchanged." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 lg:pt-40 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <p className="font-code text-xs uppercase tracking-[0.25em] text-terra mb-4">About Us</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1]">
              The legacy of {STORE.shortName}, est. {STORE.est}.
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mt-7">
              We are a family-run kirana and general store in the heart of Rajasthan's bazaar
              country. What began as one wooden counter has grown into the neighbourhood's
              most trusted shelf — without ever losing the personal scale it was built on.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2.5rem] border border-terra/40 -rotate-2" aria-hidden="true" />
              <img
                src={IMAGES.aboutStore}
                alt="Shelves inside Arjun Ram Harijan store"
                data-testid="about-hero-image"
                className="relative rounded-[2.5rem] w-full aspect-[4/3] object-cover"
              />
            </div>
          </Reveal>
        </div>

        {/* Values */}
        <section className="mt-24 lg:mt-32" data-testid="values-section">
          <Reveal>
            <p className="font-code text-xs uppercase tracking-[0.25em] text-terra mb-4">What We Stand For</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight leading-snug max-w-xl">
              Four values, weighed daily.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div
                  data-testid={`value-card-${i}`}
                  className="h-full rounded-3xl border border-border bg-card p-7 hover:border-terra/50 hover:shadow-lg hover:shadow-terra/5 transition-all duration-300"
                >
                  <div className="h-11 w-11 rounded-full bg-sage flex items-center justify-center">
                    <v.icon size={19} className="text-terra" strokeWidth={1.9} />
                  </div>
                  <h3 className="font-display text-xl font-semibold mt-5">{v.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-2.5">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Timeline / sourcing */}
        <section className="mt-24 lg:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="sourcing-section">
          <Reveal className="lg:col-span-5">
            <p className="font-code text-xs uppercase tracking-[0.25em] text-terra mb-4">Roots &amp; Sourcing</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight leading-snug">
              From Rajasthan's farms to your kitchen shelf.
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed mt-6">
              Our chillies come from Mathania, dals from Nagaur and Bikaner, mustard oil from
              Bharatpur's wooden ghanis, and dairy from a local cooperative we've partnered
              with for over thirty years. Short supply chains mean fresher goods and fairer
              prices for farmer and customer alike.
            </p>
            <div className="grid grid-cols-2 gap-5 mt-8">
              <img src={IMAGES.dairyCans} alt="Fresh dairy delivery cans" loading="lazy" data-testid="sourcing-image-dairy" className="rounded-3xl aspect-[4/5] object-cover w-full" />
              <img src={IMAGES.almondTexture} alt="Premium almonds close-up" loading="lazy" data-testid="sourcing-image-almonds" className="rounded-3xl aspect-[4/5] object-cover w-full mt-10" />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <div className="border-l border-border pl-8 space-y-10 mt-2">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.year} delay={i * 0.06} className="relative">
                  <span className="absolute -left-[37px] top-1.5 h-2.5 w-2.5 rounded-full bg-terra" aria-hidden="true" />
                  <p className="font-display text-2xl font-bold text-terra" data-testid={`timeline-year-${i}`}>{t.year}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-2 max-w-lg">{t.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <Reveal className="mt-24 lg:mt-32">
          <div className="rounded-[2.5rem] bg-primary text-primary-foreground p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8" data-testid="about-cta-banner">
            <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight leading-snug max-w-xl">
              Come say namaste at the counter — or browse the shelves online first.
            </h2>
            <div className="flex flex-wrap gap-4 shrink-0">
              <Link
                to="/products"
                data-testid="about-cta-products"
                className="group inline-flex items-center gap-2.5 rounded-full bg-terra bg-terra-hover text-white px-7 py-3.5 font-sub text-sm font-semibold transition-colors duration-300"
              >
                Browse Products
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="about-cta-contact"
                className="inline-flex items-center gap-2.5 rounded-full border border-primary-foreground/30 px-7 py-3.5 font-sub text-sm font-semibold hover:border-gold hover:text-gold transition-colors duration-300"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
